const trailStatusSourceUrl = 'https://mountainbikezug.ch/api/trailstatus/ampel';
export const trailStatusMinimumRefreshMs = 5_000;

export type TrailStatusState =
  | { readonly type: 'loading'; readonly imageUrl?: string }
  | { readonly type: 'ready'; readonly imageUrl: string }
  | { readonly type: 'unavailable'; readonly imageUrl?: string };

export interface TrailStatusEnvironment {
  readonly now: () => number;
  readonly preload: (url: string) => Promise<void>;
  readonly addWindowListener: (type: 'focus', listener: () => void) => () => void;
  readonly addVisibilityListener: (listener: () => void) => () => void;
  readonly isVisible: () => boolean;
  readonly schedule: (delayMs: number, listener: () => void) => () => void;
}

export interface TrailStatusController {
  readonly refresh: () => void;
  readonly retry: () => void;
  readonly subscribe: (listener: (state: TrailStatusState) => void) => () => void;
  readonly destroy: () => void;
}

function refreshUrl(timestamp: number): string {
  const url = new URL(trailStatusSourceUrl);
  url.searchParams.set('t', String(timestamp));
  return url.href;
}

export function createBrowserTrailStatusEnvironment(browserWindow: Window): TrailStatusEnvironment {
  const environment: TrailStatusEnvironment = {
    now: Date.now,
    preload(url: string): Promise<void> {
      return new Promise((resolve, reject) => {
        const image = browserWindow.document.createElement('img');
        image.onload = () => {
          resolve();
        };
        image.onerror = () => {
          reject(new Error('trail status image unavailable'));
        };
        image.src = url;
      });
    },
    addWindowListener(type: 'focus', listener: () => void): () => void {
      browserWindow.addEventListener(type, listener);
      return () => {
        browserWindow.removeEventListener(type, listener);
      };
    },
    addVisibilityListener(listener: () => void): () => void {
      browserWindow.document.addEventListener('visibilitychange', listener);
      return () => {
        browserWindow.document.removeEventListener('visibilitychange', listener);
      };
    },
    isVisible: () => browserWindow.document.visibilityState === 'visible',
    schedule(delayMs: number, listener: () => void): () => void {
      const timer = browserWindow.setTimeout(listener, delayMs);
      return () => {
        browserWindow.clearTimeout(timer);
      };
    },
  };
  return Object.freeze(environment);
}

export function createTrailStatusController(
  environment: TrailStatusEnvironment,
  minimumRefreshMs = trailStatusMinimumRefreshMs,
): TrailStatusController {
  const listeners = new Set<(state: TrailStatusState) => void>();
  let state: TrailStatusState = { type: 'loading' };
  let lastRequestStartedAt = Number.NEGATIVE_INFINITY;
  let requestSequence = 0;
  let inFlight = false;
  let destroyed = false;
  let cancelScheduledRetry: (() => void) | undefined;

  function emit(nextState: TrailStatusState): void {
    if (destroyed) return;
    state = Object.freeze(nextState);
    for (const listener of listeners) listener(state);
  }

  function refresh(): void {
    const startedAt = environment.now();
    if (destroyed || inFlight || startedAt - lastRequestStartedAt < minimumRefreshMs) return;
    cancelScheduledRetry?.();
    cancelScheduledRetry = undefined;
    inFlight = true;
    lastRequestStartedAt = startedAt;
    requestSequence += 1;
    const sequence = requestSequence;
    const previousImage = 'imageUrl' in state ? state.imageUrl : undefined;
    emit({ type: 'loading', ...(previousImage === undefined ? {} : { imageUrl: previousImage }) });
    const nextImage = refreshUrl(startedAt);
    void environment
      .preload(nextImage)
      .then(() => {
        if (destroyed || sequence !== requestSequence) return;
        inFlight = false;
        emit({ type: 'ready', imageUrl: nextImage });
      })
      .catch(() => {
        if (destroyed || sequence !== requestSequence) return;
        inFlight = false;
        emit({
          type: 'unavailable',
          ...(previousImage === undefined ? {} : { imageUrl: previousImage }),
        });
      });
  }

  const removeFocusListener = environment.addWindowListener('focus', refresh);
  const removeVisibilityListener = environment.addVisibilityListener(() => {
    if (environment.isVisible()) refresh();
  });
  refresh();

  function retry(): void {
    if (destroyed || inFlight || cancelScheduledRetry !== undefined) return;
    const remaining = minimumRefreshMs - (environment.now() - lastRequestStartedAt);
    if (remaining <= 0) {
      refresh();
      return;
    }
    cancelScheduledRetry = environment.schedule(remaining, () => {
      cancelScheduledRetry = undefined;
      refresh();
    });
  }

  return Object.freeze({
    refresh,
    retry,
    subscribe(listener: (state: TrailStatusState) => void): () => void {
      if (destroyed) return () => undefined;
      listeners.add(listener);
      listener(state);
      return () => listeners.delete(listener);
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      requestSequence += 1;
      cancelScheduledRetry?.();
      listeners.clear();
      removeFocusListener();
      removeVisibilityListener();
    },
  });
}
